import type {
	AnchorStrategyClusterizationModel,
	AnchorStrategySettingsGroupModel,
	FilterRuleModel,
} from '@/shared/generated'

import { keyBy, mapValues, pick, pipe } from 'lodash/fp'
import { XIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAnchorStrategyMatchedProductsQuery } from '@/entities/anchor'
import { isValidAssignments } from '@/entities/pricing-campaign'

import { RHFInlineEditText } from '@/shared/components/HookForm'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { CardHeader } from '@/shared/components/ui/card'
import { Spinner } from '@/shared/components/ui/spinner'

type MatchedProductsData = { products: number; families: number }

export const AnchorFamilyCardHeader = ({
	id,
	name,
	onClick,
}: {
	id: string
	name: string
	onClick: () => void
}) => {
	const { t } = useTranslation()

	const { watch } = useFormContext()
	const optimization_group_ids = watch('optimization_group_ids')
	const groups = watch('settings.groups')
	const isFiltersValid = isValidAssignments(
		groups?.map((group: { name: string; filters: FilterRuleModel[] }) => ({
			name: group.name,
			filters: group.filters,
		}))
	)
	// A half-filled clusterization row is rejected by the API, so keep it out of the preview
	// instead of failing every request while the tolerance is being typed.
	const previewGroups = groups?.map(
		(group: AnchorStrategySettingsGroupModel) => ({
			...group,
			clusterization: (group.clusterization || []).filter(
				({ column, percent }: AnchorStrategyClusterizationModel) =>
					!!column && percent !== null && percent !== undefined
			),
		})
	)
	const matchedProductsQuery = useAnchorStrategyMatchedProductsQuery(
		{
			groups: previewGroups || [],
			optimization_group_ids,
		},
		{
			enabled: isFiltersValid,
			select: pipe(keyBy('id'), mapValues(pick(['products', 'families']))),
		}
	)
	const matchedProducts = matchedProductsQuery.data?.[id] as
		MatchedProductsData | undefined

	return (
		<CardHeader className='flex items-center justify-between'>
			<RHFInlineEditText
				className='text-md mb-0 font-bold'
				name={name}
				placeholder={t(
					'anchoring_management.strategy.tab.anchor_families.family_name.placeholder'
				)}
			/>
			<div className='flex items-center'>
				{matchedProductsQuery?.isLoading && <Spinner />}
				{matchedProducts && (
					<Badge variant='neutral' size='sm'>
						<>
							<span>{matchedProducts?.products ?? 0} </span>
							<span className='text-xs'>
								{t('anchoring_management.strategy.matching_products.title')},
							</span>
							<span>{matchedProducts?.families ?? 0} </span>
							<span className='text-xs'>
								{t('anchoring_management.strategy.groups.title')}
							</span>
						</>
					</Badge>
				)}
				<Button variant='ghost' size='icon' onClick={onClick}>
					<XIcon />
				</Button>
			</div>
		</CardHeader>
	)
}
